// frontend/src/utils/interpretation/buildRelationshipCorrectionCandidate.js

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[’']/g, "'")
    .replace(/\s+/g, ' ');
}

function escapeRegex(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function titleCaseName(value = '') {
  return String(value)
    .trim()
    .split(/\s+/)
    .map((part) => {
      if (!part) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
}

function normalizeRole(role = '') {
  const normalized = normalizeText(role);

  const map = {
    mom: 'mother',
    mother: 'mother',
    dad: 'father',
    father: 'father',
    sis: 'sister',
    sister: 'sister',
    bro: 'brother',
    brother: 'brother',
    son: 'son',
    daughter: 'daughter',
    child: 'child',
    wife: 'wife',
    husband: 'husband',
    girlfriend: 'girlfriend',
    boyfriend: 'boyfriend',
    'ex girlfriend': 'ex-girlfriend',
    'ex-girlfriend': 'ex-girlfriend',
    'ex boyfriend': 'ex-boyfriend',
    'ex-boyfriend': 'ex-boyfriend',
    'ex wife': 'ex-wife',
    'ex-wife': 'ex-wife',
    'ex husband': 'ex-husband',
    'ex-husband': 'ex-husband',
    exwife: 'ex-wife',
    exhusband: 'ex-husband',
    grandma: 'grandmother',
    grandmother: 'grandmother',
    grandpa: 'grandfather',
    grandfather: 'grandfather',
    aunt: 'aunt',
    aunts: 'aunt',
    uncle: 'uncle',
    uncles: 'uncle',
    cousin: 'cousin',
    cousins: 'cousin',
    friend: 'friend',
    friends: 'friend',
    buddy: 'friend',
    pal: 'friend',
  };

  return map[normalized] || normalized;
}

function buildRelationshipVariants(role = '') {
  const normalized = normalizeRole(role);
  if (!normalized) return [];

  const variants = new Set([normalized]);

  const aliasGroups = {
    mother: ['mom', 'mother'],
    father: ['dad', 'father'],
    sister: ['sis', 'sister'],
    brother: ['bro', 'brother'],
    friend: ['friend', 'friends', 'buddy', 'pal'],
    grandmother: ['grandma', 'grandmother'],
    grandfather: ['grandpa', 'grandfather'],
    aunt: ['aunt', 'aunts'],
    uncle: ['uncle', 'uncles'],
    cousin: ['cousin', 'cousins'],
    'ex-wife': ['ex wife', 'ex-wife', 'exwife'],
    'ex-husband': ['ex husband', 'ex-husband', 'exhusband'],
    'ex-girlfriend': ['ex girlfriend', 'ex-girlfriend'],
    'ex-boyfriend': ['ex boyfriend', 'ex-boyfriend'],
  };

  Object.entries(aliasGroups).forEach(([canonical, aliases]) => {
    if (canonical === normalized || aliases.includes(normalized)) {
      variants.add(canonical);
      aliases.forEach((alias) => variants.add(alias));
    }
  });

  return Array.from(variants).sort((a, b) => b.length - a.length);
}

function detectNegationSignal(normalizedMessage = '') {
  const negationPatterns = [
    /\bis not my\b/i,
    /\bisn't my\b/i,
    /\bisnt my\b/i,
    /\bisn’t my\b/i,
    /\bnot my\b/i,
    /\bno longer my\b/i,
  ];

  const matched = negationPatterns.find((pattern) => pattern.test(normalizedMessage));
  if (!matched) return null;

  return {
    hasNegation: true,
    matchedBy: matched.toString(),
    confidence: 0.96,
  };
}

function findRelationshipRoleSignal(normalizedMessage = '', relationships = []) {
  if (!Array.isArray(relationships) || relationships.length === 0) return null;

  const uniqueRoles = Array.from(
    new Set(
      relationships
        .map((person) => normalizeRole(person?.relationship || person?.relationship_type || ''))
        .filter(Boolean)
    )
  );

  for (const role of uniqueRoles) {
    const variants = buildRelationshipVariants(role);

    for (const variant of variants) {
      const pattern = new RegExp(`\\b${escapeRegex(variant)}\\b`, 'i');
      if (pattern.test(normalizedMessage)) {
        return {
          role,
          matchedBy: variant,
          confidence: 0.95,
        };
      }
    }
  }

  return null;
}

function buildPersonNameVariants(person = {}) {
  const first = normalizeText(person?.first_name || '');
  const last = normalizeText(person?.last_name || '');

  const variants = [];

  if (first && last) {
    variants.push({
      variant: `${first} ${last}`,
      bindingType: 'explicit_full_name',
      bindingConfidence: 0.99,
    });
  }

  if (first) {
    variants.push({
      variant: first,
      bindingType: 'explicit_first_name',
      bindingConfidence: 0.9,
    });
  }

  return variants;
}

function findEntitySignal(normalizedMessage = '', relationships = []) {
  if (!Array.isArray(relationships) || relationships.length === 0) return null;

  const candidates = [];

  for (const person of relationships) {
    const variants = buildPersonNameVariants(person);

    for (const entry of variants) {
      const pattern = new RegExp(`\\b${escapeRegex(entry.variant)}\\b`, 'i');
      if (!pattern.test(normalizedMessage)) continue;

      candidates.push({
        entityId: person?.entityId || null,
        name: titleCaseName(
          [person?.first_name || '', person?.last_name || ''].filter(Boolean).join(' ')
        ),
        first_name: person?.first_name || '',
        last_name: person?.last_name || '',
        relationship: normalizeRole(person?.relationship || person?.relationship_type || ''),
        bindingType: entry.bindingType,
        bindingConfidence: entry.bindingConfidence,
        matchedBy: entry.variant,
        sourceRelationship: person,
      });
    }
  }

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    if (b.bindingConfidence !== a.bindingConfidence) {
      return b.bindingConfidence - a.bindingConfidence;
    }

    return b.name.length - a.name.length;
  });

  return candidates[0];
}

function findEntityByLastReference(lastReferencedEntity = null, relationships = []) {
  if (!lastReferencedEntity || !Array.isArray(relationships) || relationships.length === 0) {
    return null;
  }

  const referencedName = normalizeText(lastReferencedEntity?.name || '');
  const referencedRole = normalizeRole(lastReferencedEntity?.role || '');

  if (!referencedName) return null;

  const matches = relationships.filter((person) => {
    const first = normalizeText(person?.first_name || '');
    const last = normalizeText(person?.last_name || '');
    const full = [first, last].filter(Boolean).join(' ');
    const role = normalizeRole(person?.relationship || person?.relationship_type || '');

    const sameName = referencedName === first || referencedName === full;
    if (!sameName) return false;

    if (referencedRole && role && referencedRole !== role) return false;

    return true;
  });

  if (matches.length !== 1) return null;

  const person = matches[0];

  return {
    entityId: person?.entityId || null,
    name: titleCaseName(
      [person?.first_name || '', person?.last_name || ''].filter(Boolean).join(' ')
    ),
    first_name: person?.first_name || '',
    last_name: person?.last_name || '',
    relationship: normalizeRole(person?.relationship || person?.relationship_type || ''),
    bindingType: 'last_referenced_entity',
    bindingConfidence: 0.82,
    matchedBy: 'last_referenced_entity',
    sourceRelationship: person,
  };
}

function chooseEntitySignal({
  normalizedMessage = '',
  relationships = [],
  lastReferencedEntity = null,
}) {
  const explicit = findEntitySignal(normalizedMessage, relationships);
  if (explicit) return explicit;

  const byReference = findEntityByLastReference(lastReferencedEntity, relationships);
  if (byReference) return byReference;

  return null;
}

function calculateOverallConfidence({
  entitySignal = null,
  roleSignal = null,
  negationSignal = null,
}) {
  const values = [
    entitySignal?.bindingConfidence || 0,
    roleSignal?.confidence || 0,
    negationSignal?.confidence || 0,
  ].filter(Boolean);

  if (!values.length) return 0;
  return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2));
}

export default function buildRelationshipCorrectionCandidate({
  message = '',
  relationships = [],
  lastReferencedEntity = null,
}) {
  const normalizedMessage = normalizeText(message);
  if (!normalizedMessage) return null;

  const negationSignal = detectNegationSignal(normalizedMessage);
  if (!negationSignal) return null;

  const roleSignal = findRelationshipRoleSignal(normalizedMessage, relationships);
  if (!roleSignal) return null;

  const entitySignal = chooseEntitySignal({
    normalizedMessage,
    relationships,
    lastReferencedEntity,
  });

  if (!entitySignal) return null;

  const relationshipMatchesEntity =
    !entitySignal.relationship || entitySignal.relationship === roleSignal.role;

  const roleConfidence = relationshipMatchesEntity ? roleSignal.confidence : 0.35;
  const overallConfidence = calculateOverallConfidence({
    entitySignal,
    roleSignal: { ...roleSignal, confidence: roleConfidence },
    negationSignal,
  });

  return {
    type: 'relationship_correction',
    intent: 'remove_relationship',
    confidence: overallConfidence,
    negation: true,
    entity: {
      entityId: entitySignal.entityId || null,
      name: entitySignal.name,
      first_name: entitySignal.first_name || null,
      last_name: entitySignal.last_name || null,
      bindingType: entitySignal.bindingType,
      bindingConfidence: entitySignal.bindingConfidence,
    },
    relationship: {
      role: roleSignal.role,
      confidence: roleConfidence,
    },
    signals: {
      hasNegation: true,
      hasRelationshipKeyword: true,
      hasEntity: true,
      relationshipMatchesEntity,
      negationMatchedBy: negationSignal.matchedBy,
      relationshipMatchedBy: roleSignal.matchedBy,
      entityMatchedBy: entitySignal.matchedBy,
    },
    sourceRelationship: entitySignal.sourceRelationship || null,
    sourceText: message,
  };
}